import { useEffect } from 'react';

import { FrequentPassageRequest } from '@legacy-types';

import { useEvent } from '@legacy-providers/event';

import { getCrewMemberOptionsForRegistration } from '@shared/crew-members/endpoints/getCrewMemberOptionsForRegistration';
import { Form, useForm } from '@shared/forms/components/Form';
import { SelectField } from '@shared/forms/components/SelectField';
import { TextField } from '@shared/forms/components/TextField';
import { CreateFrequentPassageRequestData } from '@shared/frequent-passage-requests/endpoints/createFrequentPassageRequest';
import { frequentPassageRequestVehicleTypeOptions } from '@shared/frequent-passage-requests/enums/FrequentPassageRequestVehicleType';
import { getFrequentPassageStickerTypeOptionsForEvent } from '@shared/frequent-passage-sticker-types/endpoints/getFrequentPassageStickerTypeOptionsForEvent';
import { getRegistrationOptionsForEvent } from '@shared/registrations/endpoints/getRegistrationOptionsForEvent';
import { cn } from '@shared/utils/helpers';

type FrequentPassageRequestFormProps = {
  registrationId?: number | null;
  action: (values: CreateFrequentPassageRequestData) => Promise<FrequentPassageRequest>;
  onSave?: (response: FrequentPassageRequest) => void;
  onSaveAndReturn?: () => void;
  onCancel?: () => void;
};

export function FrequentPassageRequestForm({
  registrationId,
  action,
  onSave,
  onSaveAndReturn,
  onCancel,
}: FrequentPassageRequestFormProps) {
  const event = useEvent();

  const { form, fields } = useForm<CreateFrequentPassageRequestData>({
    defaults: {
      registrationId,
    },
  });

  useEffect(() => {
    if (form.getValues().registrationId !== registrationId) {
      form.setValue('registrationId', registrationId ?? null);
    }
  }, [registrationId]);

  const selectedRegistrationId = form.watch('registrationId');

  useEffect(() => {
    form.resetField('crewMemberId');
  }, [selectedRegistrationId]);

  return (
    <Form action={action} onSave={onSave} onSaveAndReturn={onSaveAndReturn} onCancel={onCancel} form={form}>
      {!registrationId && (
        <SelectField
          name={fields.registrationId}
          label="Registration"
          options={(query) => getRegistrationOptionsForEvent(event.id, query)}
          required
        />
      )}
      <fieldset
        key={selectedRegistrationId}
        className={cn('grid gap-6', { 'opacity-50': !selectedRegistrationId })}
        disabled={!selectedRegistrationId}
      >
        <SelectField
          name={fields.crewMemberId}
          label="Crew Member"
          options={(query) => getCrewMemberOptionsForRegistration(selectedRegistrationId!, query)}
          required
        />
        <div className="md:grid grid-cols-2 gap-8">
          <SelectField
            name={fields.vehicleType}
            label="Vehicle Type"
            options={frequentPassageRequestVehicleTypeOptions}
            required
          />
          <TextField name={fields.licensePlate} label="License Plate" />
        </div>
        <SelectField
          name={fields.frequentPassageStickerTypeId}
          label="Sticker Type"
          options={(query) => getFrequentPassageStickerTypeOptionsForEvent(event.id, query)}
          required
        />
      </fieldset>
    </Form>
  );
}
